import React, { useState } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { forgotPassword, verifyOTP } from '../../features/auth/authSlice';
import { showToast } from '../../features/ui/uiSlice';
import { Link, useNavigate } from 'react-router-dom';

const emailSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
});

const otpSchema = yup.object({
    otp: yup.string()
        .required('OTP is required')
        .length(6, 'OTP must be 6 digits')
        .matches(/^\d{6}$/, 'OTP must contain only digits'),
});

type EmailFormData = yup.InferType<typeof emailSchema>;
type OTPFormData = yup.InferType<typeof otpSchema>;

const ForgotPassword: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const emailForm = useForm<EmailFormData>({
        resolver: yupResolver(emailSchema),
    });

    const otpForm = useForm<OTPFormData>({
        resolver: yupResolver(otpSchema),
    });

    const onEmailSubmit = async (data: EmailFormData) => {
        setIsLoading(true);
        try {
            await dispatch(forgotPassword(data.email)).unwrap();
            setEmail(data.email);
            setStep('otp');
            setResendCooldown(60);
            const interval = setInterval(() => {
                setResendCooldown((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            dispatch(showToast({ message: 'OTP sent to your email', type: 'success' }));
        } catch (error: any) {
            dispatch(showToast({ message: error || 'Failed to send OTP', type: 'error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const onOTPSubmit = async (data: OTPFormData) => {
        setIsLoading(true);
        try {
            await dispatch(verifyOTP({ email, otp: data.otp })).unwrap();
            navigate('/reset-password', { state: { email, otp: data.otp } });
        } catch (error: any) {
            dispatch(showToast({ message: error || 'Invalid OTP', type: 'error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (resendCooldown > 0) return;
        setIsLoading(true);
        try {
            await dispatch(forgotPassword(email)).unwrap();
            setResendCooldown(60);
            const interval = setInterval(() => {
                setResendCooldown((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            dispatch(showToast({ message: 'OTP resent to your email', type: 'success' }));
        } catch (error: any) {
            dispatch(showToast({ message: error || 'Failed to resend OTP', type: 'error' }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                    </div>
                    <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
                        {step === 'email' ? 'Forgot Password' : 'Verify OTP'}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {step === 'email'
                            ? 'Enter your email address and we\'ll send you an OTP to reset your password'
                            : `Enter the 6-digit OTP sent to ${email}`
                        }
                    </p>
                    <div className="mt-4">
                        <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400">
                            ← Back to Login
                        </Link>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8">
                    {step === 'email' ? (
                        <form className="space-y-6" onSubmit={emailForm.handleSubmit(onEmailSubmit)}>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email address
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    className={`mt-1 input-field ${emailForm.formState.errors.email ? 'input-field-error' : ''}`}
                                    placeholder="Enter your email"
                                    {...emailForm.register('email')}
                                />
                                {emailForm.formState.errors.email && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {emailForm.formState.errors.email.message}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="spinner mr-2"></div>
                                        Sending OTP...
                                    </div>
                                ) : (
                                    'Send OTP'
                                )}
                            </button>
                        </form>
                    ) : (
                        <form className="space-y-6" onSubmit={otpForm.handleSubmit(onOTPSubmit)}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Email Address
                                </label>
                                <div className="mt-1 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                                    {email}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Enter OTP
                                </label>
                                <input
                                    id="otp"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    className={`mt-1 input-field text-center text-2xl tracking-widest ${otpForm.formState.errors.otp ? 'input-field-error' : ''}`}
                                    placeholder="000000"
                                    autoFocus
                                    {...otpForm.register('otp')}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        otpForm.setValue('otp', value, { shouldValidate: true });
                                    }}
                                />
                                {otpForm.formState.errors.otp && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {otpForm.formState.errors.otp.message}
                                    </p>
                                )}
                            </div>

                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={handleResendOTP}
                                    disabled={resendCooldown > 0 || isLoading}
                                    className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 disabled:text-gray-400 disabled:cursor-not-allowed"
                                >
                                    {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                                </button>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="spinner mr-2"></div>
                                        Verifying...
                                    </div>
                                ) : (
                                    'Verify OTP'
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => setStep('email')}
                                className="w-full text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400"
                            >
                                Change email address
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
